import React, { useState, useEffect, useRef } from 'react';
import { sarbRatesApi } from '../lib/api';
import { formatDate, formatPercentage } from '../lib/utils';
import {
  FileText, Upload, Plus, TrendingUp, TrendingDown, Calendar, Download, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const SARBRatesPage = () => {
  const [rates, setRates] = useState([]);
  const [currentRates, setCurrentRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [rateType, setRateType] = useState('all');

  useEffect(() => {
    loadRates();
    loadCurrentRates();
  }, [rateType]);

  const loadRates = async () => {
    setLoading(true);
    try {
      const params = (rateType && rateType !== 'all') ? { rate_type: rateType } : {};
      const response = await sarbRatesApi.list(params);
      setRates(response.data);
    } catch (error) {
      console.error('Failed to load rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentRates = async () => {
    try {
      const response = await sarbRatesApi.current();
      setCurrentRates(response.data);
    } catch (error) {
      console.error('Failed to load current rates:', error);
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="sarb-rates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-2">
            RATE MANAGEMENT
          </p>
          <h1 className="font-display text-3xl font-bold text-[#1f1f1f]">
            Interest Rates
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="outline"
            data-testid="upload-rates-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload CSV/Excel
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-[#1f1f1f] hover:bg-[#333333]"
            data-testid="add-rate-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Rate
          </Button>
        </div>
      </div>

      {/* Current Rates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[#1f1f1f] p-6 text-white" data-testid="current-repo-rate">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                SARB REPO RATE
              </p>
              <p className="text-5xl font-bold font-display">
                {currentRates?.repo_rate?.rate ? formatPercentage(currentRates.repo_rate.rate) : '-'}
              </p>
              <p className="text-gray-400 text-sm mt-3">
                Effective: {currentRates?.repo_rate?.effective_date || 'N/A'}
              </p>
            </div>
            <div className="p-3 bg-white/10">
              <TrendingDown className="w-8 h-8 text-[#C5A059]" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            The repurchase (repo) rate is the rate at which the SARB lends money to commercial banks
          </p>
        </div>

        <div className="bg-gradient-to-r from-[#C5A059] to-[#D4B16A] p-6 text-white" data-testid="current-zaronia-rate">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-2">
                ZARONIA
              </p>
              <p className="text-5xl font-bold font-display">
                {currentRates?.zaronia?.rate ? formatPercentage(currentRates.zaronia.rate) : '-'}
              </p>
              <p className="text-white/70 text-sm mt-3">
                Effective: {currentRates?.zaronia?.effective_date || 'N/A'}
              </p>
            </div>
            <div className="p-3 bg-white/20">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
          </div>
          <p className="text-xs text-white/60 mt-4">
            South African Rand Overnight Index Average - replacing JIBAR by end 2026
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-50 border border-blue-200 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Rate Data Integration</p>
          <p className="text-blue-600 mt-1">
            You can upload rate data from SARB's website in CSV or Excel format. The system will parse
            and store the rates automatically for use in loan and deposit calculations.
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-4">
        <Select value={rateType} onValueChange={setRateType}>
          <SelectTrigger className="w-48" data-testid="rate-type-filter">
            <SelectValue placeholder="All Rate Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Rate Types</SelectItem>
            <SelectItem value="repo_rate">Repo Rate</SelectItem>
            <SelectItem value="zaronia">ZARONIA</SelectItem>
            <SelectItem value="prime_rate">Prime Rate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Rate History */}
      <div className="bg-white border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-[#1f1f1f]">Rate History</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full" data-testid="rates-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Rate Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Effective Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Added
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : rates.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No rates found
                  </td>
                </tr>
              ) : (
                rates.map((rate) => (
                  <tr key={rate.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-medium ${
                        rate.rate_type === 'repo_rate' ? 'bg-blue-100 text-blue-800' :
                        rate.rate_type === 'zaronia' ? 'bg-amber-100 text-amber-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {rate.rate_type === 'repo_rate' ? 'Repo Rate' :
                         rate.rate_type === 'zaronia' ? 'ZARONIA' :
                         rate.rate_type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{rate.effective_date}</td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-lg font-bold text-[#1f1f1f]">{formatPercentage(rate.rate)}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 capitalize">{rate.source}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(rate.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Rate Modal */}
      <AddRateModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          loadRates();
          loadCurrentRates();
        }}
      />

      {/* Upload Modal */}
      <UploadRatesModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          setShowUploadModal(false);
          loadRates();
          loadCurrentRates();
        }}
      />
    </div>
  );
};

const AddRateModal = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    rate_type: '', effective_date: '', rate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await sarbRatesApi.create({
        ...formData,
        rate: parseFloat(formData.rate),
        source: 'manual'
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add rate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" aria-describedby="add-rate-desc">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Add SARB Rate</DialogTitle>
        </DialogHeader>
        <p id="add-rate-desc" className="sr-only">Add a new SARB rate entry</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Rate Type *</Label>
            <Select value={formData.rate_type} onValueChange={(v) => setFormData(prev => ({ ...prev, rate_type: v }))}>
              <SelectTrigger data-testid="add-rate-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="repo_rate">Repo Rate</SelectItem>
                <SelectItem value="zaronia">ZARONIA</SelectItem>
                <SelectItem value="prime_rate">Prime Rate</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Effective Date *</Label>
            <Input
              type="date"
              value={formData.effective_date}
              onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
              required
              data-testid="add-rate-date"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Rate (%) *</Label>
            <Input
              type="number"
              step="0.01"
              value={formData.rate}
              onChange={(e) => setFormData(prev => ({ ...prev, rate: e.target.value }))}
              placeholder="e.g., 8.25"
              required
              data-testid="add-rate-value"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="submit-rate">
              {loading ? 'Adding...' : 'Add Rate'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const UploadRatesModal = ({ open, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setError('');

    try {
      const response = await sarbRatesApi.upload(file);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (result) {
      onSuccess();
    } else {
      onClose();
    }
    setFile(null);
    setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" aria-describedby="upload-rates-desc">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Upload SARB Rates</DialogTitle>
        </DialogHeader>
        <p id="upload-rates-desc" className="sr-only">Upload SARB rates from a CSV or Excel file</p>

        <div className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {result ? (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3">
              <p className="font-medium">Upload Successful</p>
              <p className="text-sm mt-1">{result.message}</p>
            </div>
          ) : (
            <>
              <div className="p-6 border-2 border-dashed border-gray-300 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-2">
                  Upload CSV or Excel file from SARB website
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="rate-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Select File
                </Button>
                {file && (
                  <p className="text-sm text-gray-500 mt-3">
                    Selected: {file.name}
                  </p>
                )}
              </div>

              <div className="p-4 bg-gray-50 border border-gray-200 text-sm">
                <p className="font-medium text-gray-900 mb-2">Expected Format</p>
                <p className="text-gray-600">
                  The file should contain columns for: Rate Type, Effective Date, Rate/Value
                </p>
                <p className="text-gray-500 mt-2 text-xs">
                  Example headers: "rate_type", "effective_date", "rate" or "Rate Type", "Date", "Value"
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          {!result && (
            <Button onClick={handleUpload} disabled={!file || loading} className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="upload-rates-submit">
              {loading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SARBRatesPage;
